import { useState } from 'react';
import { Download, FileText, FileJson, FileSpreadsheet, ChevronDown } from 'lucide-react';
import { generatePdfReport, ReportData } from '../lib/generatePdf';
import { generateCsvReport } from '../lib/generateCsv';
import { generateJsonReport } from '../lib/generateJson';
import { useTranslation } from 'react-i18next';
import { sileo } from 'sileo';

interface ExportButtonProps {
  data?: ReportData;
}

/**
 * Export Report Button
 * Formats: PDF, CSV, JSON
 */
export function ExportButton({ data }: ExportButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  const exportFormats = [
    {
      name: t('export.pdfReport'),
      icon: <FileText className="h-4 w-4" />,
      format: 'pdf',
      description: t('export.pdfDesc')
    },
    {
      name: t('export.csvData'),
      icon: <FileSpreadsheet className="h-4 w-4" />,
      format: 'csv',
      description: t('export.csvDesc')
    },
    {
      name: t('export.jsonData'),
      icon: <FileJson className="h-4 w-4" />,
      format: 'json',
      description: t('export.jsonDesc')
    },
  ];

  const handleExport = (format: string) => {
    if (!data) {
      console.error("No data provided for export");
      return;
    }

    console.log(`Exporting report as ${format.toUpperCase()}...`);

    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `devlint-report-${data.project.name.toLowerCase().replace(/\s+/g, '-')}-${dateStr}`;

    try {
      let blob: Blob | null = null;
      let extension = '';

      switch (format) {
        case 'pdf':
          blob = generatePdfReport(data);
          extension = 'pdf';
          break;
        case 'csv':
          blob = generateCsvReport(data);
          extension = 'csv';
          break;
        case 'json':
          blob = generateJsonReport(data);
          extension = 'json';
          break;
      }

      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filename}.${extension}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        const formatIcon =
          format === 'pdf' ? <FileText className="size-3.5" />
            : format === 'csv' ? <FileSpreadsheet className="size-3.5" />
              : <FileJson className="size-3.5" />;

        sileo.success({
          title: t('toasts.exportSuccess'),
          description: t('toasts.exportSuccessDesc', { format: format.toUpperCase() }),
          icon: formatIcon,
        });
      }
    } catch (error) {
      console.error(`Error generating ${format} report:`, error);
      sileo.error({
        title: t('toasts.exportError'),
        description: t('toasts.exportErrorDesc', { format: format.toUpperCase() }),
      });
    }

    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 rounded-md bg-primary px-md py-sm text-text-inverse hover:bg-primary-hover transition-colors font-mono"
      >
        <Download className="h-4 w-4" />
        <span>{t('export.exportReport')}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop to close dropdown when clicking outside */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown Menu */}
          <div className="absolute right-0 mt-2 w-64 rounded-lg bg-surface border border-border shadow-lg z-20 overflow-hidden">
            {exportFormats.map((format) => (
              <button
                key={format.format}
                onClick={() => handleExport(format.format)}
                className="w-full flex items-start gap-3 px-4 py-3 hover:bg-surface-hover transition-colors text-left border-b border-border last:border-b-0"
              >
                <div className="text-primary mt-0.5">{format.icon}</div>
                <div className="flex-1">
                  <div className="font-medium text-text-primary font-mono text-sm">
                    {format.name}
                  </div>
                  <div className="text-xs text-text-secondary font-mono mt-0.5">
                    {format.description}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
